/**
 * DgsnReports.jsx — DGSN Daily Police Report Management
 * Rently.ma · Morocco Fleet OS
 *
 * V2 rewrite: CSS variables, ui.jsx imports, AbortController,
 * i18n t() translations, formatters, responsive table, 4-state handling.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { dgsn as dgsnApi } from '../services/api';
import { formatDate, formatShortDate } from '../utils/formatters';
import {
  PageHeader, Badge, StatCard, EmptyState,
  SkeletonTable, GlassModal, SearchInput,
} from '../components/ui.jsx';

// ── Status mapping (labelKey pattern — translated at render) ────────
const STATUS_CFG = {
  pending:   { labelKey: 'dgsn.statusPending',   variant: 'warning' },
  submitted: { labelKey: 'dgsn.statusSubmitted',  variant: 'success' },
  accepted:  { labelKey: 'dgsn.statusAccepted',   variant: 'info' },
  rejected:  { labelKey: 'dgsn.statusRejected',   variant: 'danger' },
  draft:     { labelKey: 'dgsn.statusDraft',      variant: 'neutral' },
};

function statusOf(report) {
  if (report.submitted_at || !report.pending_submission) return STATUS_CFG.submitted;
  return STATUS_CFG.pending;
}

// ── Generate Report Modal ───────────────────────────────────────────
function GenerateModal({ branchId, onClose, onGenerate }) {
  const { t } = useTranslation();
  const [reportDate, setReportDate] = useState(new Date().toISOString().slice(0, 10));
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState(null);

  const handleGenerate = async () => {
    setGenerating(true);
    setGenError(null);
    try {
      await onGenerate({ branch_id: branchId, report_date: reportDate });
      onClose();
    } catch (e) {
      setGenError(e.message || t('common.error'));
    }
    setGenerating(false);
  };

  return (
    <GlassModal open title={t('dgsn.generate')} subtitle={t('dgsn.subtitle')} onClose={onClose} width={440}>
      <div style={{ padding: 'var(--space-xl)', display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)' }}>
        <div>
          <label style={{
            fontSize: 'var(--text-label)', color: 'var(--text-secondary)',
            textTransform: 'uppercase', letterSpacing: '0.06em',
            marginBottom: 4, display: 'block',
          }}>
            {t('dgsn.reportDate')}
          </label>
          <input
            type="date" value={reportDate}
            onChange={e => setReportDate(e.target.value)}
            style={{
              width: '100%', padding: '10px 12px',
              background: 'var(--raised)',
              border: '1px solid var(--border-medium)',
              borderRadius: 'var(--radius-sm)',
              color: 'var(--text)', fontSize: 13,
              fontFamily: 'inherit', outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>

        <div style={{
          background: 'var(--gold-glow)', border: '1px solid var(--border-gold)',
          borderRadius: 'var(--radius-sm)', padding: 'var(--space-md)',
          fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6,
        }}>
          {t('dgsn.autoGenerateInfo')}
        </div>

        {genError && (
          <div style={{ padding: '10px 14px', background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.25)', borderRadius: 'var(--radius-sm)', fontSize: 12, color: '#C53030', lineHeight: 1.4 }}>
            {genError}
          </div>
        )}
        <div style={{ display: 'flex', gap: 'var(--space-md)' }}>
          <button onClick={onClose} style={{
            flex: 1, padding: 12, background: 'transparent',
            border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
            color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer',
            fontFamily: 'inherit',
          }}>{t('common.cancel')}</button>
          <button onClick={handleGenerate} disabled={generating} style={{
            flex: 1, padding: 12,
            background: generating ? 'var(--text-muted)' : 'var(--gold)',
            color: 'var(--bg)', border: 'none',
            borderRadius: 'var(--radius-sm)', fontSize: 13, fontWeight: 700,
            cursor: generating ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit', letterSpacing: '0.02em',
          }}>
            {generating ? t('dgsn.generating') : t('dgsn.generateBtn')}
          </button>
        </div>
      </div>
    </GlassModal>
  );
}

// ── Report Preview Modal ────────────────────────────────────────────
function ReportPreviewModal({ report, entries, onClose, onSubmit }) {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const isPending = !report.submitted_at;

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      await onSubmit(report.id);
      onClose();
    } catch (e) {
      setSubmitError(e.message || t('dgsn.submitError'));
    }
    setSubmitting(false);
  };

  const st = statusOf(report);

  return (
    <GlassModal
      open title={`${t('dgsn.policeReport')} — ${report.report_date}`}
      subtitle={`${report.branch_name} · ${t('dgsn.entriesCount', { count: entries.length })}`}
      onClose={onClose} width={640}
    >
      {/* Screen-only content */}
      <div className="no-print" style={{ padding: 'var(--space-xl)' }}>
        {/* Status row */}
        <div style={{ marginBottom: 'var(--space-lg)', display: 'flex', justifyContent: 'flex-end' }}>
          <Badge label={t(st.labelKey)} variant={st.variant} />
        </div>

        {entries.length === 0 ? (
          <div style={{ padding: 'var(--space-2xl)', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
            {t('dgsn.noEntries')}
          </div>
        ) : (
          <div className="responsive-table" style={{ marginBottom: 'var(--space-lg)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>
                  {[t('dgsn.fullName'), t('dgsn.nationality'), t('dgsn.cinPassport'), t('dgsn.vehicle'), t('dgsn.checkIn'), t('dgsn.checkOut')].map(h => (
                    <th key={h} style={{
                      padding: '10px 12px', textAlign: 'left',
                      fontSize: 'var(--text-label)', color: 'var(--text-secondary)',
                      letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 600,
                      borderBottom: '1px solid var(--border)',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {entries.map(e => (
                  <tr key={e.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td data-label={t('dgsn.fullName')} style={{ padding: '10px 12px', color: 'var(--text)', fontWeight: 600 }}>
                      {e.renter_full_name}
                    </td>
                    <td data-label={t('dgsn.nationality')} style={{ padding: '10px 12px', color: 'var(--text-secondary)' }}>
                      {e.renter_nationality || '—'}
                    </td>
                    <td data-label={t('dgsn.cinPassport')} style={{ padding: '10px 12px', fontFamily: 'var(--font-mono)', color: 'var(--gold)', fontSize: 11 }}>
                      {e.renter_passport || e.renter_cin || '—'}
                    </td>
                    <td data-label={t('dgsn.vehicle')} style={{ padding: '10px 12px', fontFamily: 'var(--font-mono)', color: 'var(--text)', fontSize: 11 }}>
                      {e.vehicle_plate}
                    </td>
                    <td data-label={t('dgsn.checkIn')} style={{ padding: '10px 12px', color: 'var(--text-secondary)', fontSize: 11 }}>
                      {e.check_in_date ? formatDate(e.check_in_date) : '—'}
                    </td>
                    <td data-label={t('dgsn.checkOut')} style={{ padding: '10px 12px', color: 'var(--text-secondary)', fontSize: 11 }}>
                      {e.check_out_date ? formatDate(e.check_out_date) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {submitError && (
          <div style={{ marginBottom: 'var(--space-md)', padding: '10px 14px', background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.25)', borderRadius: 'var(--radius-sm)', fontSize: 12, color: '#C53030', lineHeight: 1.4 }}>
            {submitError}
          </div>
        )}
        <div style={{ display: 'flex', gap: 'var(--space-md)' }}>
          <button onClick={onClose} style={{
            flex: 1, padding: 12, background: 'transparent',
            border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
            color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
          }}>{t('common.close')}</button>
          {isPending && entries.length > 0 && (
            <button onClick={handleSubmit} disabled={submitting} style={{
              flex: 1, padding: 12,
              background: submitting ? 'var(--text-muted)' : 'var(--green)',
              color: '#fff', border: 'none', borderRadius: 'var(--radius-sm)',
              fontSize: 13, fontWeight: 700,
              cursor: submitting ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
            }}>
              {submitting ? t('dgsn.submitting') : t('dgsn.markSubmitted')}
            </button>
          )}
          <button onClick={() => window.print()} style={{
            padding: '12px 20px', background: 'transparent',
            border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
            color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
          }}>{t('dgsn.print')}</button>
        </div>
      </div>

      {/* Print-only content */}
      <div className="print-only" style={{ display: 'none', padding: '20mm' }}>
        <div className="print-header">
          <h1>{t('dgsn.policeReport')}</h1>
          <h2>{report.report_date}</h2>
        </div>

        <div className="print-meta">
          <p><strong>Agence:</strong> {report.branch_name}</p>
          <p><strong>Statut:</strong> {report.submitted_at ? t('dgsn.statusSubmitted') : t('dgsn.statusPending')}</p>
          <p><strong>Nombre d'entrées:</strong> {entries.length}</p>
          <p><strong>Généré le:</strong> {new Date().toLocaleString('fr-MA', { dateStyle: 'long', timeStyle: 'short' })}</p>
        </div>

        {entries.length === 0 ? (
          <div className="print-empty-state">
            Aucune entrée pour cette période.
          </div>
        ) : (
          <table className="avoid-break">
            <thead>
              <tr>
                <th>{t('dgsn.fullName')}</th>
                <th>{t('dgsn.nationality')}</th>
                <th>{t('dgsn.cinPassport')}</th>
                <th>{t('dgsn.vehicle')}</th>
                <th>{t('dgsn.checkIn')}</th>
                <th>{t('dgsn.checkOut')}</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e, idx) => (
                <tr key={idx}>
                  <td>{e.renter_full_name}</td>
                  <td>{e.renter_nationality || '—'}</td>
                  <td>{e.renter_passport || e.renter_cin || '—'}</td>
                  <td>{e.vehicle_plate}</td>
                  <td>{e.check_in_date ? formatDate(e.check_in_date) : '—'}</td>
                  <td>{e.check_out_date ? formatDate(e.check_out_date) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="print-footer">
          <p>Page 1 de 1</p>
          <p>Imprimé le {new Date().toLocaleString('fr-MA', { dateStyle: 'long', timeStyle: 'short' })}</p>
          <p>Rently.ma — Système de Gestion de Flotte</p>
        </div>
      </div>
    </GlassModal>
  );
}

// ── Main Page ───────────────────────────────────────────────────────
export default function DgsnReports() {
  const { t } = useTranslation();
  const [reports, setReports] = useState([]);
  const [pendingDays, setPendingDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showGenerate, setShowGenerate] = useState(false);
  const [previewReport, setPreviewReport] = useState(null);
  const [previewEntries, setPreviewEntries] = useState([]);
  const [branchId, setBranchId] = useState(null);
  const [search, setSearch] = useState('');

  const abortRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const results = await Promise.allSettled([
        dgsnApi.reports(),
        dgsnApi.pending(),
      ]);

      const [reportsRes, pendingRes] = results;

      // FIXED: No mock fallbacks — show real errors if backend fails
      if (reportsRes.status === 'fulfilled' && reportsRes.value) {
        const reps = reportsRes.value.reports || reportsRes.value || [];
        setReports(reps);
        if (reps.length > 0) setBranchId(reps[0].branch_id);
      } else {
        setError(reportsRes.reason?.message || t('system.serviceUnavailable'));
        setReports([]);
      }

      if (pendingRes.status === 'fulfilled' && pendingRes.value) {
        const pdays = pendingRes.value.pending_days || [];
        setPendingDays(pdays);
        if (pdays.length > 0) setBranchId(pdays[0].branch_id);
      } else {
        setError(pendingRes.reason?.message || t('system.serviceUnavailable'));
        setPendingDays([]);
      }

      if (reportsRes.status === 'fulfilled' && pendingRes.status === 'fulfilled') {
        setError(null);
      }
    } catch (e) {
      if (e.name === 'AbortError') return;
      setError(e.message || t('system.serviceUnavailable'));
      setReports([]);
      setPendingDays([]);
    }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    abortRef.current = controller;
    fetchData();
    const iv = setInterval(fetchData, 30000);
    return () => {
      controller.abort();
      clearInterval(iv);
    };
  }, [fetchData]);

  const handleGenerate = async (data) => {
    await dgsnApi.generate(data);
    fetchData();
  };

  const handleSubmit = async (reportId) => {
    await dgsnApi.submit(reportId);
    fetchData();
  };

  const handleViewReport = async (report) => {
    try {
      const data = await dgsnApi.reportByDate(report.report_date, report.branch_id);
      setPreviewReport(data.report || report);
      setPreviewEntries(data.report?.entries || []);
    } catch (e) {
      setError(e.message || t('common.error'));
    }
  };

  // Compute stats
  const totalReports = reports.length;
  const today = new Date().toISOString().slice(0, 10);
  const todayCount = reports.filter(r => r.report_date === today).length;
  const pendingCount = reports.filter(r => r.pending_submission).length;

  // Filtered reports (search by date or branch name)
  const filteredReports = search.trim()
    ? reports.filter(r =>
        (r.report_date || '').includes(search) ||
        (r.branch_name || '').toLowerCase().includes(search.toLowerCase())
      )
    : reports;

  // ── Loading state ─────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ animation: 'fadeInUp 400ms var(--ease-spring) both' }}>
        <PageHeader title={t('dgsn.title')} subtitle={t('dgsn.subtitle')} />
        <div className="grid-stats" style={{ marginBottom: 'var(--space-xl)' }}>
          {[0, 1, 2].map(i => (
            <div key={i} className="skeleton" style={{ height: 90, borderRadius: 'var(--radius)' }} />
          ))}
        </div>
        <SkeletonTable rows={4} />
      </div>
    );
  }

  // ── Error state ───────────────────────────────────────────────────
  if (error) {
    return (
      <div style={{ animation: 'fadeInUp 400ms var(--ease-spring) both' }}>
        <PageHeader title={t('dgsn.title')} subtitle={t('dgsn.subtitle')} />
        <div style={{
          padding: 'var(--space-xl)', background: 'var(--status-alert-bg)',
          border: '1px solid var(--red)', borderRadius: 'var(--radius)',
          textAlign: 'center',
        }}>
          <div style={{ color: 'var(--red)', fontSize: 14, fontWeight: 600, marginBottom: 'var(--space-sm)' }}>
            {error}
          </div>
          <button onClick={() => { setLoading(true); setError(null); fetchData(); }} style={{
            padding: '8px 20px', background: 'transparent',
            border: '1px solid var(--red)', borderRadius: 'var(--radius-sm)',
            color: 'var(--red)', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
          }}>{t('common.retry')}</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ animation: 'fadeInUp 400ms var(--ease-spring) both' }}>
      {/* Header */}
      <PageHeader
        title={t('dgsn.title')}
        subtitle={t('dgsn.subtitle')}
        action={
          <button onClick={() => setShowGenerate(true)} style={{
            padding: '10px 20px', height: 44,
            background: 'var(--gold)', color: 'var(--bg)',
            border: 'none', borderRadius: 'var(--radius-sm)',
            fontSize: 13, fontWeight: 700, cursor: 'pointer',
            fontFamily: 'inherit', letterSpacing: '0.02em',
            whiteSpace: 'nowrap',
          }}>
            + {t('dgsn.generate')}
          </button>
        }
      />

      {/* Pending Days Alert */}
      {pendingDays.length > 0 && (
        <div style={{
          padding: 'var(--space-md) var(--space-lg)',
          background: 'rgba(251,191,36,0.08)',
          border: '1px solid rgba(251,191,36,0.2)',
          borderRadius: 'var(--radius-sm)',
          marginBottom: 'var(--space-lg)',
          fontSize: 12, color: 'var(--orange)',
          display: 'flex', alignItems: 'center', gap: 'var(--space-sm)',
        }}>
          <span style={{
            width: 8, height: 8, borderRadius: '50%',
            background: 'var(--orange)', display: 'inline-block', flexShrink: 0,
          }} />
          <span>
            <strong>{t('dgsn.pendingDaysAlert', { count: pendingDays.length })}</strong>
            {' '}{pendingDays.map(d => d.rental_date).join(', ')}
          </span>
        </div>
      )}

      {/* Stat Cards */}
      <div className="grid-stats" style={{ marginBottom: 'var(--space-xl)' }}>
        <StatCard label={t('dgsn.totalReports')} value={totalReports} index={0} />
        <StatCard label={t('dgsn.today')} value={todayCount} index={1} />
        <StatCard label={t('dgsn.pending')} value={pendingCount} index={2} />
      </div>

      {/* Search + Table or Empty State */}
      {reports.length === 0 ? (
        <EmptyState
          icon={null}
          title={t('dgsn.empty')}
          description={t('dgsn.emptyDesc')}
          ctaLabel={t('dgsn.generate')}
          onCta={() => setShowGenerate(true)}
        />
      ) : (
        <>
          {/* Search bar */}
          <div style={{ marginBottom: 'var(--space-lg)', display: 'flex', gap: 'var(--space-md)', alignItems: 'center' }}>
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder={t('dgsn.searchPlaceholder')}
              style={{ maxWidth: 320 }}
            />
            {search && filteredReports.length !== reports.length && (
              <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>
                {filteredReports.length} / {reports.length}
              </span>
            )}
          </div>
        <div className="responsive-table">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-body)' }}>
            <thead>
              <tr>
                {[t('narsa.date'), t('dgsn.agency'), t('dgsn.entries'), t('common.status'), t('dgsn.submittedAt'), t('common.actions')].map(h => (
                  <th key={h} style={{
                    padding: '12px 16px', textAlign: 'left',
                    fontSize: 'var(--text-label)', color: 'var(--text-secondary)',
                    letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 600,
                    borderBottom: '1px solid var(--border)',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredReports.map((report, idx) => {
                const st = statusOf(report);
                return (
                  <tr
                    key={report.id || idx}
                    style={{
                      borderBottom: '1px solid var(--border)',
                      transition: 'background var(--duration-fast) var(--ease-smooth)',
                      animation: `scaleIn 300ms var(--ease-smooth) both`,
                      animationDelay: `${idx * 50}ms`,
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--raised)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <td data-label={t('narsa.date')} style={{
                      padding: '12px 16px',
                      fontFamily: 'var(--font-mono)', color: 'var(--gold)',
                      fontSize: 13, fontWeight: 600,
                    }}>
                      {report.report_date}
                    </td>
                    <td data-label={t('dgsn.agency')} style={{ padding: '12px 16px', color: 'var(--text)', fontSize: 12 }}>
                      {report.branch_name || '—'}
                    </td>
                    <td data-label={t('dgsn.entries')} style={{
                      padding: '12px 16px',
                      fontFamily: 'var(--font-mono)', color: 'var(--text)', fontSize: 13,
                    }}>
                      {report.entry_count}
                    </td>
                    <td data-label={t('common.status')} style={{ padding: '12px 16px' }}>
                      <Badge label={t(st.labelKey)} variant={st.variant} />
                    </td>
                    <td data-label={t('dgsn.submittedAt')} style={{ padding: '12px 16px', color: 'var(--text-secondary)', fontSize: 12 }}>
                      {report.submitted_at ? formatShortDate(report.submitted_at) : '—'}
                    </td>
                    <td data-label={t('common.actions')} style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
                        <button onClick={() => handleViewReport(report)} style={{
                          padding: '4px 12px',
                          background: 'var(--gold-glow)',
                          color: 'var(--gold)',
                          border: '1px solid var(--border-gold)',
                          borderRadius: 6, fontSize: 11, fontWeight: 600,
                          cursor: 'pointer', fontFamily: 'inherit',
                        }}>
                          {t('dgsn.view')}
                        </button>
                        {report.pending_submission && (
                          <button onClick={() => handleSubmit(report.id)} style={{
                            padding: '4px 12px',
                            background: 'rgba(74,222,128,0.08)',
                            color: 'var(--green)',
                            border: '1px solid rgba(74,222,128,0.25)',
                            borderRadius: 6, fontSize: 11, fontWeight: 600,
                            cursor: 'pointer', fontFamily: 'inherit',
                          }}>
                            {t('dgsn.submitBtn')}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
      )}

      {/* Summary bar */}
      {reports.length > 0 && (
        <div style={{
          marginTop: 'var(--space-lg)',
          padding: 'var(--space-md) var(--space-lg)',
          background: 'var(--card)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          fontSize: 13, color: 'var(--text-secondary)',
        }}>
          <span>
            <strong style={{ color: 'var(--text)' }}>{totalReports}</strong> {t('dgsn.reportsCount')} ·{' '}
            <strong style={{ color: 'var(--text)' }}>{pendingCount}</strong> {t('dgsn.pending').toLowerCase()}
          </span>
          <span style={{
            fontFamily: 'var(--font-mono)', fontWeight: 600,
            color: 'var(--gold)',
          }}>
            {t('dgsn.policeFile')}
          </span>
        </div>
      )}

      {/* Modals */}
      {showGenerate && branchId && (
        <GenerateModal
          branchId={branchId}
          onClose={() => setShowGenerate(false)}
          onGenerate={handleGenerate}
        />
      )}
      {previewReport && (
        <ReportPreviewModal
          report={previewReport}
          entries={previewEntries}
          onClose={() => { setPreviewReport(null); setPreviewEntries([]); }}
          onSubmit={handleSubmit}
        />
      )}
    </div>
  );
}
